import type { AuthUser, UserRole } from './types';

export const ROLE_LABELS: Record<UserRole, string> = {
  super_admin: 'Super Admin',
  tenant_admin: 'Tenant Admin',
  tenant_member: 'Member',
  viewer: 'Viewer',
};

export type NavKey =
  | 'dashboard'
  | 'analytics'
  | 'billing'
  | 'platform-billing'
  | 'platform-plans'
  | 'platform-tenant-billing'
  | 'platform-invoices'
  | 'my-plan'
  | 'knowledge-gaps'
  | 'knowledge-answers'
  | 'handoffs'
  | 'tickets'
  | 'agent-actions'
  | 'meetings'
  | 'call-logs'
  | 'phone-numbers'
  | 'routing-policies'
  | 'live-calls'
  | 'voice-engines'
  | 'telephony-countries'
  | 'porting-requests'
  | 'tenants'
  | 'platform-users'
  | 'platform-roles'
  | 'platform-audit'
  | 'users'
  | 'roles'
  | 'audit'
  | 'agents'
  | 'integrations'
  | 'knowledge'
  | 'settings'
  | 'profile'
  | 'security';

/** Permission(s) required to see a nav item. Empty = always (authenticated). */
const NAV_ANY_OF: Record<NavKey, string[]> = {
  dashboard: ['platform.dashboard.read'],
  analytics: ['tenant.analytics_outcomes.read'],
  billing: ['tenant.billing.read'],
  'platform-billing': ['platform.billing.read'],
  'platform-plans': ['platform.billing.read'],
  'platform-tenant-billing': ['platform.billing.read'],
  'platform-invoices': ['platform.billing.read'],
  'my-plan': ['tenant.billing.read'],
  'knowledge-gaps': ['tenant.knowledge_gaps.read'],
  'knowledge-answers': ['tenant.knowledge_answers.read'],
  handoffs: ['tenant.handoffs.read'],
  tickets: ['tenant.tickets.read'],
  'agent-actions': ['tenant.agent_actions.read'],
  meetings: ['tenant.meetings.read'],
  'call-logs': ['tenant.call_logs.read'],
  // Telephony (BE 1.2; Phone Numbers page: Prompt 1.13).
  'phone-numbers': ['tenant.phone_numbers.read', 'tenant.telephony.read'],
  'routing-policies': ['tenant.routing_policies.read'],
  'live-calls': ['tenant.live_calls.read'],
  'voice-engines': ['platform.telephony.read'],
  'telephony-countries': ['platform.telephony.read'],
  'porting-requests': ['platform.telephony.read'],
  tenants: ['platform.tenants.read'],
  'platform-users': ['platform.platform_users.read'],
  'platform-roles': ['platform.platform_roles.read'],
  'platform-audit': ['platform.platform_audit.read'],
  users: ['tenant.tenant_users.read'],
  roles: ['tenant.tenant_roles.read'],
  audit: ['tenant.audit_log.read'],
  agents: ['tenant.agents.read'],
  integrations: ['tenant.integrations.view'],
  knowledge: ['tenant.knowledge_documents.read'],
  settings: [],
  profile: [],
  security: [],
};

export function isSuperAdmin(
  userOrRole?: AuthUser | UserRole | null,
): boolean {
  if (!userOrRole) return false;
  if (typeof userOrRole === 'string') return userOrRole === 'super_admin';
  return userOrRole.isSuperAdmin === true || userOrRole.role === 'super_admin';
}

export function can(
  user: AuthUser | null | undefined,
  permission: string,
): boolean {
  if (!user) return false;
  if (isSuperAdmin(user)) return true;
  return user.permissions.includes(permission);
}

export function canAny(
  user: AuthUser | null | undefined,
  permissions: string[],
): boolean {
  if (!user) return false;
  if (permissions.length === 0) return true;
  if (isSuperAdmin(user)) return true;
  return permissions.some((p) => user.permissions.includes(p));
}

export function canAll(
  user: AuthUser | null | undefined,
  permissions: string[],
): boolean {
  if (!user) return false;
  if (permissions.length === 0) return true;
  if (isSuperAdmin(user)) return true;
  return permissions.every((p) => user.permissions.includes(p));
}

/**
 * Agent-scoped check: role permission + grant (deny-by-default unless unrestricted).
 */
export function canAgent(
  user: AuthUser | null | undefined,
  agentId: string,
  permission: string,
): boolean {
  if (!can(user, permission)) return false;
  if (!user || isSuperAdmin(user) || user.agentsUnrestricted) return true;
  const grant = user.agentGrants[agentId];
  if (!grant) return false;
  if (grant.preset === 'full') return true;
  if (grant.preset === 'view') {
    return permission.endsWith('.read') || permission.includes('.read');
  }
  return grant.permissions.includes(permission);
}

/**
 * May add platform-controlled tenant permissions (e.g.
 * `tenant.telephony_byo.manage`) to a tenant role — mirrors the BE rule
 * (super admin or `platform.telephony.manage`).
 */
export function canGrantPlatformControlledCodes(
  user: AuthUser | null | undefined,
): boolean {
  return isSuperAdmin(user) || can(user, 'platform.telephony.manage');
}

/** True when user may act-as any tenant (platform switcher). */
export function canActAsTenant(user: AuthUser | null | undefined): boolean {
  return isSuperAdmin(user) || can(user, 'platform.tenants.act_as');
}

/** Show tenant switcher for act-as users or multi-membership users. */
export function shouldShowTenantSwitcher(
  user: AuthUser | null | undefined,
): boolean {
  if (!user) return false;
  if (canActAsTenant(user)) return true;
  return user.memberships.length > 1;
}

export function canAccessNav(
  user: AuthUser | null | undefined,
  key: NavKey,
): boolean {
  if (!user) return false;
  if (key === 'agents') {
    if (can(user, 'tenant.agents.read')) return true;
    // Grant-only custom roles still need Agents nav when they have grants.
    return Object.keys(user.agentGrants ?? {}).length > 0;
  }
  const required = NAV_ANY_OF[key];
  if (!required || required.length === 0) return true;
  return canAny(user, required);
}

/**
 * First admin path the user may open (sidebar order). Used after login and
 * when a gated route denies access — never hardcode `/admin` alone.
 */
export const ADMIN_LANDING_ROUTES: ReadonlyArray<{ key: NavKey; path: string }> =
  [
    { key: 'dashboard', path: '/admin' },
    { key: 'analytics', path: '/admin/analytics' },
    { key: 'billing', path: '/admin/billing' },
    { key: 'platform-billing', path: '/admin/platform-billing' },
    { key: 'knowledge-gaps', path: '/admin/knowledge-gaps' },
    { key: 'knowledge-answers', path: '/admin/knowledge-answers' },
    { key: 'handoffs', path: '/admin/handoffs' },
    { key: 'tickets', path: '/admin/tickets' },
    { key: 'agent-actions', path: '/admin/agent-actions' },
    { key: 'meetings', path: '/admin/meetings' },
    { key: 'call-logs', path: '/admin/call-logs' },
    { key: 'phone-numbers', path: '/admin/phone-numbers' },
    { key: 'routing-policies', path: '/admin/routing-policies' },
    { key: 'live-calls', path: '/admin/live-calls' },
    { key: 'voice-engines', path: '/admin/voice-engines' },
    { key: 'telephony-countries', path: '/admin/telephony-countries' },
    { key: 'porting-requests', path: '/admin/porting-requests' },
    { key: 'tenants', path: '/admin/tenants' },
    { key: 'platform-users', path: '/admin/platform-users' },
    { key: 'platform-roles', path: '/admin/platform-roles' },
    { key: 'platform-audit', path: '/admin/platform-audit' },
    { key: 'users', path: '/admin/users' },
    { key: 'roles', path: '/admin/roles' },
    { key: 'audit', path: '/admin/audit' },
    { key: 'agents', path: '/admin/agents' },
    { key: 'integrations', path: '/admin/integrations' },
    { key: 'knowledge', path: '/admin/knowledge' },
    { key: 'settings', path: '/admin/settings' },
    { key: 'profile', path: '/admin/profile' },
    { key: 'security', path: '/admin/security' },
  ];

export function getDefaultAdminRoute(
  user: AuthUser | null | undefined,
): string {
  if (!user) return '/login';
  if (user.flags?.mustEnrollMfa) return '/admin/security';
  // Dashboard (`/admin`) is a platform (cross-tenant) view. Only platform users
  // land there; tenant users fall through to Analytics / their first accessible
  // view (D4).
  const isPlatformUser = canActAsTenant(user);
  for (const { key, path } of ADMIN_LANDING_ROUTES) {
    if (key === 'dashboard' && !isPlatformUser) continue;
    if (canAccessNav(user, key)) return path;
  }
  return '/admin/profile';
}

/** True when a path is allowed for this user (best-effort path → nav mapping). */
export function canAccessPath(
  user: AuthUser | null | undefined,
  path: string,
): boolean {
  if (!user) return false;
  const normalized = path.split('?')[0] || '/admin';
  if (normalized === '/admin' || normalized === '/admin/') {
    return canAccessNav(user, 'dashboard');
  }
  const match = ADMIN_LANDING_ROUTES.find(
    (r) => r.path !== '/admin' && normalized.startsWith(r.path),
  );
  if (match) return canAccessNav(user, match.key);
  return normalized.startsWith('/admin');
}

/** @deprecated Prefer can(user, 'tenant.agents.create') etc. */
export const canManageTenant = (
  userOrRole?: AuthUser | UserRole | null,
): boolean => {
  if (!userOrRole) return false;
  if (typeof userOrRole === 'string') {
    return userOrRole === 'super_admin' || userOrRole === 'tenant_admin';
  }
  return (
    isSuperAdmin(userOrRole) ||
    canAny(userOrRole, [
      'tenant.agents.create',
      'tenant.agents.update',
      'tenant.vapi_credentials.update',
      'tenant.knowledge_documents.upload',
    ])
  );
};

/** @deprecated Prefer canAny for handoffs/tickets. */
export const canOperate = (
  userOrRole?: AuthUser | UserRole | null,
): boolean => {
  if (!userOrRole) return false;
  if (typeof userOrRole === 'string') {
    return (
      userOrRole === 'super_admin' ||
      userOrRole === 'tenant_admin' ||
      userOrRole === 'tenant_member'
    );
  }
  return (
    isSuperAdmin(userOrRole) ||
    canAny(userOrRole, [
      'tenant.handoffs.read',
      'tenant.tickets.read',
      'tenant.conversations.message',
    ])
  );
};

export const canViewAnalytics = (
  userOrRole?: AuthUser | UserRole | null,
): boolean => {
  if (!userOrRole) return false;
  if (typeof userOrRole === 'string') return !!userOrRole;
  return can(userOrRole, 'tenant.analytics_outcomes.read') || isSuperAdmin(userOrRole);
};

/** Display label for the user badge (membership role or platform / legacy). */
export function sessionRoleLabel(user: AuthUser | null | undefined): string {
  if (!user) return '';
  if (user.isSuperAdmin) return ROLE_LABELS.super_admin;
  const activeId = user.activeContext.tenantId ?? user.tenantId;
  const membership = user.memberships.find((m) => m.tenantId === activeId);
  if (membership?.roleName) {
    const key = membership.roleName as UserRole;
    return ROLE_LABELS[key] ?? membership.roleName;
  }
  if (user.platformRoles[0]) return user.platformRoles[0];
  return ROLE_LABELS[user.role] ?? user.role;
}
